/**
 * Theme, router, and the authenticated frame. `AuthGate` wraps everything, so no
 * route renders until an operator is signed in and carries the `operator` claim.
 */
import { createTheme, CssBaseline, ThemeProvider } from '@mui/material';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthGate } from './auth';
import { Layout } from './components/Layout';
import { HomePage } from './pages/HomePage';
import { HostPage } from './pages/HostPage';
import { InstructionPage } from './pages/InstructionPage';
import { ReleasePage } from './pages/ReleasePage';
import { ReleasesPage } from './pages/ReleasesPage';

/**
 * A quiet variant of the MUI defaults. The page is a set of outlined cards, so
 * the canvas is tinted a step off white to make them read as surfaces rather
 * than as boxes drawn on a blank sheet; headings are weighted so the hierarchy
 * (page title → card title → fact labels) is visible without size jumps; and
 * buttons drop their shadow because every action here is already framed by the
 * card that explains it.
 */
const theme = createTheme({
    palette: { mode: 'light', background: { default: '#f6f7f9' } },
    shape: { borderRadius: 8 },
    typography: {
        h5: { fontWeight: 600 },
        h6: { fontWeight: 600, fontSize: '1.0625rem' },
        overline: { fontWeight: 600, letterSpacing: '0.06em' },
    },
    components: {
        MuiButton: { defaultProps: { disableElevation: true } },
        MuiAppBar: { styleOverrides: { root: { backgroundColor: '#fff' } } },
    },
});

export function App() {
    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <BrowserRouter>
                <AuthGate>
                    <Layout>
                        <Routes>
                            <Route path="/" element={<HomePage />} />
                            <Route path="/hosts/:role" element={<HostPage />} />
                            <Route path="/releases" element={<ReleasesPage />} />
                            <Route path="/releases/:version" element={<ReleasePage />} />
                            {/*
                              * The diff is its own URL rather than a tab, so a link in
                              * Slack can point at the files a release changed.
                              */}
                            <Route path="/releases/:version/changes" element={<ReleasePage showChanges />} />
                            <Route path="/instructions/:host/:id" element={<InstructionPage />} />
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </Layout>
                </AuthGate>
            </BrowserRouter>
        </ThemeProvider>
    );
}
